import { graphqlRequest, throwIfGraphQLError } from './graphqlClient.js';
import type { ShellHydration } from './hydration.js';

// PublicSurvey intentionally excludes partialSaveEnabled + schedule — the server
// enforces schedule before responding, and partial save defaults to on client-side.
const SURVEY_FIELDS = `
  _id title description type status
  pages {
    key order title description isWelcome isThankYou
    buttonText buttonIcon showTimeEstimate
    media { url type alt position width }
    mediaLayout imageFit imagePosition imageOpacity
    socialLinks { platform url label }
    redirectUrl redirectLabel redirectDelay
    logic {
      defaultHidden defaultNextPageKey
      rules {
        id name enabled
        conditionGroup { conjunction conditions { questionKey variableKey operator value } }
        actions { type targetKey }
      }
    }
  }
  questions {
    key pageKey order type label description required showNumber uiHint
    media { url type alt imagePosition imageFit imageOpacity imageHeight }
    settings {
      options { label value }
      randomize allowOther placeholder min max step
      lowLabel highLabel minLabel maxLabel showLabels
      defaultValue rows columns allowedFileTypes maxFileSize minLength maxLength
      consentText markdownContent iframeUrl emojiIcons
    }
    validationRules { type value message }
    logic {
      defaultHidden
      rules {
        id name enabled
        conditionGroup { conjunction conditions { questionKey variableKey operator value } }
        actions { type targetKey }
      }
    }
  }
  theme {
    key name
    colors { background card accent button text textMuted }
    font size layout cornerRadius
    branding { showLogo logoUrl brandName showFooter footerText }
    backgroundImage backgroundImageFit backgroundImagePosition backgroundImageOpacity
  }
  appearance {
    displayMode layout accentColor backgroundColor
    showProgressBar showQuestionNumbers allowMultipleSubmissions
    randomizeQuestions hideBranding
  }
  identityConfig { mode emailLabel emailHelpText }
  inApp {
    enabled scope urlContains delaySeconds frequency displayStyle nudgeHeadline
  }
  logicRules {
    id name enabled
    conditionGroup {
      conjunction
      conditions { questionKey variableKey operator value }
    }
    actions { type targetKey }
  }
  variables { key label source defaultValue }
  quotas {
    id name limit currentCount action
    conditionGroup { conjunction conditions { questionKey variableKey operator value } }
  }
  paths {
    id name color priority enabled pageKeys exitTargetPageKey
    entryCondition { conjunction conditions { questionKey variableKey operator value } }
  }
  thankYouBranches {
    id name priority thankYouPageKey
    conditionGroup {
      conjunction
      conditions { questionKey variableKey operator value }
    }
  }
`;

export async function fetchPublicSurvey(hydration: ShellHydration, publicId: string): Promise<unknown> {
  const response = await graphqlRequest<{ getPublicSurvey: unknown }>(hydration, {
    query: `query GetPublicSurvey($publicId: String!) { getPublicSurvey(publicId: $publicId) { ${SURVEY_FIELDS} } }`,
    variables: { publicId },
    operationName: 'GetPublicSurvey',
  });
  return response.data?.getPublicSurvey ?? null;
}

export interface SubmitSurveyResponseInput {
  publicId: string;
  answers: unknown[];
  email?: string;
  uniqueId?: string;
  responseId?: string;
  status?: 'partial' | 'completed';
  meta?: Record<string, unknown>;
}

export interface SubmitSurveyResponseResult {
  _id?: string;
  status?: string;
}

export type SurveyEventType =
  | 'viewed'
  | 'started'
  | 'question_seen'
  | 'answered'
  | 'completed'
  | 'dismissed'
  | 'validation_failed';

export interface TrackSurveyEventInput {
  campaignId: string;
  eventType: SurveyEventType;
  questionKey?: string;
  pathId?: string;
  channel?: string;
  responseId?: string;
  sessionId?: string;
  anonymousId?: string;
  projectUserId?: string;
  meta?: Record<string, unknown>;
}

const TRACK_SURVEY_EVENT_MUTATION = `mutation TrackSurveyEvent($input: TrackSurveyEventInput!) {
  trackSurveyEvent(input: $input) { _id }
}`;

// Per-question events (question_seen, answered) come in bursts as the user
// navigates the survey. Sending each one as a separate fetch on hot paths
// blocks JS execution and floods the network panel. The buffer below holds
// events for up to BUFFER_INTERVAL_MS or until BUFFER_MAX_SIZE is hit, then
// fires them in parallel via Promise.all. Flush on pagehide uses fetch with
// keepalive: true so the requests survive even if the user closes the tab.
interface PendingEvent {
  hydration: ShellHydration;
  input: TrackSurveyEventInput;
}

const BUFFER_MAX_SIZE = 10;
const BUFFER_INTERVAL_MS = 2000;
const PAGEHIDE_FLUSH_LIMIT = 64 * 1024; // sendBeacon-equivalent ceiling for keepalive fetch.

const surveyEventQueue: PendingEvent[] = [];
let surveyEventFlushTimer: ReturnType<typeof setTimeout> | null = null;
let pagehideHookInstalled = false;

export async function trackSurveyEvent(
  hydration: ShellHydration,
  input: TrackSurveyEventInput,
): Promise<void> {
  if (!input.campaignId) return;
  ensurePagehideHook();
  surveyEventQueue.push({ hydration, input });

  if (surveyEventQueue.length >= BUFFER_MAX_SIZE) {
    await flushSurveyEventQueue();
    return;
  }
  if (!surveyEventFlushTimer) {
    surveyEventFlushTimer = setTimeout(() => {
      void flushSurveyEventQueue();
    }, BUFFER_INTERVAL_MS);
  }
}

export async function flushSurveyEventQueue(options: { keepalive?: boolean } = {}): Promise<void> {
  if (surveyEventFlushTimer) {
    clearTimeout(surveyEventFlushTimer);
    surveyEventFlushTimer = null;
  }
  if (surveyEventQueue.length === 0) return;
  const batch = surveyEventQueue.splice(0);
  await Promise.allSettled(batch.map((entry) => sendSurveyEventOverWire(entry, options)));
}

async function sendSurveyEventOverWire(
  entry: PendingEvent,
  options: { keepalive?: boolean },
): Promise<void> {
  const body = JSON.stringify({
    query: TRACK_SURVEY_EVENT_MUTATION,
    variables: {
      input: {
        projectId: entry.hydration.projectId,
        campaignId: entry.input.campaignId,
        eventType: entry.input.eventType,
        questionKey: entry.input.questionKey,
        pathId: entry.input.pathId,
        channel: entry.input.channel ?? 'inApp',
        responseId: entry.input.responseId,
        sessionId: entry.input.sessionId,
        anonymousId: entry.input.anonymousId,
        projectUserId: entry.input.projectUserId,
        meta: entry.input.meta,
      },
    },
    operationName: 'TrackSurveyEvent',
  });
  // Skip keepalive for oversize bodies — browsers reject and the request
  // would silently drop. This protects against a long meta payload (e.g.
  // referrer with many query params) tripping the limit on tab close.
  const useKeepalive = Boolean(options.keepalive) && body.length <= PAGEHIDE_FLUSH_LIMIT;
  try {
    await fetch(`${entry.hydration.apiHost}/api/graphql`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: useKeepalive,
    });
  } catch {
    // Survey events are best-effort — losing one doesn't break the user
    // flow. Backend's unique index handles cross-device dedup.
  }
}

function ensurePagehideHook(): void {
  if (pagehideHookInstalled) return;
  if (typeof window === 'undefined') return;
  pagehideHookInstalled = true;
  // pagehide fires on bfcache exit AND tab close — covers both cases where
  // visibilitychange wouldn't (Safari aggressive bfcache eviction). visibilitychange
  // is the secondary trigger for browsers where the tab can be backgrounded
  // for a long time before pagehide fires.
  const flush = (): void => {
    if (surveyEventQueue.length === 0) return;
    void flushSurveyEventQueue({ keepalive: true });
  };
  window.addEventListener('pagehide', flush);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

export async function submitSurveyResponse(
  hydration: ShellHydration,
  input: SubmitSurveyResponseInput,
): Promise<SubmitSurveyResponseResult | null> {
  const response = await graphqlRequest<{ submitSurveyResponse: SubmitSurveyResponseResult | null }>(hydration, {
    query: `mutation SubmitSurveyResponse($input: SubmitSurveyResponseInput!) {
      submitSurveyResponse(input: $input) { _id status }
    }`,
    variables: {
      input: {
        publicId: input.publicId,
        answers: input.answers,
        email: input.email,
        anonymousId: input.uniqueId,
        responseId: input.responseId,
        channel: 'inApp',
        status: input.status ?? 'completed',
        meta: input.meta,
      },
    },
    operationName: 'SubmitSurveyResponse',
  });
  throwIfGraphQLError(response, 'survey submit failed');
  return response.data?.submitSurveyResponse ?? null;
}
